<template>
  <v-card height="'650px'" class="card">
    <v-card-title class="pa-0 card-title d-flex align-center">
      <span>時間別シェア率</span>
      <AlertTooltip
        v-if="chartAlert"
        class="ml-2"
        text="取得データボリュームが少なく統計上の信頼性が低い日が含まれています。該当日は参考値としてご参照ください。"
      />
      <v-spacer />
      <ChartDescriptionTooltip
        menu-key="chainCompare"
        sub-menu-key="trendVisitor"
        chart-key="timeOfDayShare"
        class="mr-27px"
      />
      <DownloadButton
        label="CSVデータ"
        :disabled="props.selectedChainIds.length === 0"
        :get-file-id="
          () =>
            downloadVisitCountChartCSV(
              props.selectedChainIds,
              props.selectedPrefectureIds.map((id) => id.toString()),
              PARTICLE_TYPE.HOUR,
              startDate,
              endDate
            )
        "
        :csv-name="`チェーン比較_時間別シェア率_${convertSlashDelimiter(
          startDate
        )}-${convertSlashDelimiter(endDate)}`"
      />
    </v-card-title>
    <v-container class="pa-0" fluid>
      <v-row class="my-0" dense>
        <v-col class="py-0">
          <div v-show="props.selectedChainIds.length === 0" class="unselected_card">
            チェーンを選択するとチャートが表示されます。
          </div>
          <div v-show="props.selectedChainIds.length > 0">
            <LoadingImg v-if="chartLoading" :height="`${105 * props.selectedChainIds.length}px`" />
            <NoDataChart v-else-if="isNoData" />
            <div v-else>
              <!-- 平日チャート -->
              <ShareChart
                :header="weekdayChartHeader"
                :charts="weekdayChartData"
                :color-styles="heatmapBlueStyles"
              />
              <!-- 土日祝日チャート -->
              <ShareChart :header="holidayChartHeader" :charts="holidayChartData" />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script setup lang="ts">
import AlertTooltip from '@/commons/components/Elements/AlertTooltip.vue'
import ChartDescriptionTooltip from '@/commons/components/Elements/ChartDescriptionTooltip.vue'
import DownloadButton from '@/commons/components/Elements/DownloadButton.vue'
import LoadingImg from '@/commons/components/loadingImg.vue'
import { DAYS, PARTICLE_TYPE, SUB_TYPE } from '@/commons/enums'
import { Chain } from '@/commons/interfaces'
import { getVisitCountGraph } from '@/features/ChainAnalytics/visitor/axios'
import { VisitCountGraph } from '@/features/ChainAnalytics/visitor/interfaces/response'
import NoDataChart from '@/features/ShopAnalytics/components/Common/noDataChart.vue'
import ShareChart from '@/features/StoreCompare/trendVisitor/components/ShareChart.vue'
import { ChartData } from '@/features/StoreCompare/trendVisitor/types'
import { processTimeChart } from '@/features/StoreCompare/trendVisitor/utils'
import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import * as notify from '@/plugins/notification'
import { convertSlashDelimiter } from '@/commons/utils/dateUtil'
import { downloadVisitCountChartCSV } from '../axios'
import { isEqualSorted } from '@/commons/utils/chain'

/* --------------------------------------------------------------------------
  props
 ---------------------------------------------------------------------------*/

const props = withDefaults(
  defineProps<{
    startDate: string,
    endDate: string,
    selectedChainIds?: string[]
    selectedPrefectureIds?: number[]
  }>(),
  {
    selectedChainIds: (): string[] => [],
    selectedPrefectureIds: (): number[] => []
  }
)

/* --------------------------------------------------------------------------
  Vuex
 ---------------------------------------------------------------------------*/

const store = useStore()
const startDate = computed<string>(() => store.state.startDate)
const endDate = computed<string>(() => store.state.endDate)
const chainMap = computed<Map<string, Chain>>(() => store.getters.chainMap)

/* --------------------------------------------------------------------------
  core
 ---------------------------------------------------------------------------*/

const selectedChains = computed<Chain[]>(() =>
  props.selectedChainIds.map((id) => chainMap.value.get(id)).filter(Boolean) as Chain[]
)

const chartLoading = ref<boolean>(true)
const chartResponses = ref<
  Map<{ orderIndex: number; chainName: string; }, VisitCountGraph>
>(new Map())
const chartData = computed<ChartData>(() =>
  processTimeChart(
    Array.from(chartResponses.value).map(([{ orderIndex, chainName }, { visitCounts }]) => ({
      storeName: chainName, // HACK: リファクタでチェーン比較用の process 関数を作成する場合は chainName を受け取るよう修正する
      color: "", // 曜日／時間別人数では使ってないが店舗比較・来店人数推移と同じ型を使いまわしているので src/features/StoreCompare/types/index.t ComparisonGraph
      orderIndex,
      visitCounts
    }))
  )
)
const chartAlert = computed<boolean>(() => {
  if (chartResponses.value.size === 0) return false
  return Array.from(chartResponses.value.values()).some(({ visitCounts }) =>
    visitCounts.chartItems.some(({ isAlert }) => isAlert)
  )
})
const isNoData = computed<boolean>(() => {
  if (chartResponses.value.size === 0) return true
  return Array.from(chartResponses.value.values()).every(({ visitCounts }) =>
    visitCounts.chartItems.every(({ value }) => !value)
  )
})
const baseChartHeader = ['08-10時', '11-13時', '14-16時', '17-19時', '20-22時']

const fetchChart = async () => {
  chartLoading.value = true

  chartResponses.value.clear()

  const fetchings = selectedChains.value.map((chain, index) =>
    getVisitCountGraph(
      chain.id,
      startDate.value,
      endDate.value,
      PARTICLE_TYPE.HOUR,
      SUB_TYPE.NONE,
      props.selectedPrefectureIds
    ).then((res) => {
      chartResponses.value.set(
        {
          // NOTE: チェーンのランク順ではなく選択順とする。
          orderIndex: index,
          chainName: chain.name,
        },
        res.data
      )
    })
  )

  try {
    await Promise.all(fetchings)
  } catch {
    notify.notifyErrorMessage('時間別シェア率チャートが表示できませんでした。')
  } finally {
    chartLoading.value = false
  }
}

const isReadyToFetch = () => {
  return props.selectedChainIds.length > 0 && props.selectedPrefectureIds.length > 0 && chainMap.value.size > 0
}

/* --------------------------------------------------------------------------
  平日
 ---------------------------------------------------------------------------*/

const weekdayChartHeader = [DAYS.WEEKDAYS['text'], ...baseChartHeader]

const weekdayChartData = computed<ChartData>(() =>
  chartData.value.map((value) =>
    // 奇数のデータは平日データ
    value.filter((_, index) => index === 0 || index % 2 !== 0)
  )
)

const heatmapBlueStyles = [
  'background: #B4D7F0',
  'background: #ABD1EC',
  'background: #98C1E0',
  'background: #85B2D5',
  'background: #72A3C9; color: #ffffff;',
  'background: #5F93BD; color: #ffffff;',
  'background: #4C84B1; color: #ffffff;',
  'background: #3A76A7; color: #ffffff;',
  'background: #26669A; color: #ffffff;',
  'background: #14588F; color: #ffffff;'
]

/* --------------------------------------------------------------------------
  土日祝日
 ---------------------------------------------------------------------------*/

const holidayChartHeader = [DAYS.HOLIDAYS['text'], ...baseChartHeader]

const holidayChartData = computed<ChartData>(() =>
  chartData.value.map((value) =>
    // 偶数のデータは土日祝日データ
    value.filter((_, index) => index % 2 === 0)
  )
)

/* --------------------------------------------------------------------------
  created
 ---------------------------------------------------------------------------*/

if (isReadyToFetch()) {
  fetchChart()
}

/* --------------------------------------------------------------------------
  watch
 ---------------------------------------------------------------------------*/

watch(
  () => [props.selectedChainIds, props.selectedPrefectureIds, props.startDate, props.endDate],
  (newVal, oldVal) => {
    if (isReadyToFetch() && !isEqualSorted(newVal, oldVal)) {
      fetchChart()
    }
  },
  { deep: true }
)

watch(
  () => chainMap.value.size > 0, // chainMap自体を見てしまうと期間を変更してチェーンが読み直しされた際に重複してリクエストが走ってしまう
  () => {
    if (isReadyToFetch()) {
      fetchChart()
    }
  },
)
</script>

<style lang="scss" scoped>
.mr-27px {
  margin-right: 27px;
}
</style>
